import AddBlock from '@clearkit/icons/glyphs/AddBlock';
import DirectionForward from '@clearkit/icons/glyphs/DirectionForward';
import Trash from '@clearkit/icons/glyphs/Trash';
import Unknown from '@clearkit/icons/glyphs/Unknown';
import {
  CKAttributeSelector,
  CKBox,
  CKButton,
  CKSwitch,
  CKTooltip,
} from '@clearkit/react';
import capitalize from 'lodash/capitalize';

import {
  attributeForFinder,
  backendSupportedDataType,
  findAttribute,
  readableType,
  sourceIcon,
} from '~lib/dataEnrichment';
import { isTypeMismatch } from '~lib/isTypeMismatch';

import ServiceIcon from '../ServiceIcon';

export default function FieldMappingGroup({
  group,
  type,
  objectType,
  onChange,
  onGroupChange,
  onRemove,
  onRemoveMapping,
  sourceAttributes,
  targetAttributes,
  addMapping,
  showError,
  errors,
}) {
  const inErrorState = (attribute) => {
    return !!errors[attribute] || (!attribute && showError);
  };

  return (
    <CKBox
      variant={CKBox.Variant['tinted-frame']}
      className="flex flex-col items-start p-4 -mx-4 grid">
      <div className={'mapping-group pb-2'}>
        <div className={'flex flex-row content-center'}>
          <p className="text-base font-medium text-gray-600">Address Group</p>
          <CKTooltip
            tooltip={
              <p className="text-base font-normal font-medium">
                Address groups allow multiple mappings to respect the same
                overwrite setting. This is most often used for linking multiple
                address components (i.e. state and country) to ensure they
                always match.
              </p>
            }
            arrowPlacement="bottom"
            options={{ placement: 'top' }}
            animated={false}>
            <div className="flex pt-1 ml-2 text-gray-300 outline-none">
              <Unknown width="16" height="16" />
            </div>
          </CKTooltip>
        </div>
        <div className="pl-4">
          <CKSwitch
            id={`mappings[${group.groupId}].overwrite`}
            checked={!group.conditionalOverwrite}
            srOnly={false}
            onChange={() =>
              onGroupChange({
                conditionalOverwrite: !group.conditionalOverwrite,
              })
            }>
            {group.conditionalOverwrite ? 'Disabled' : 'Enabled'}
          </CKSwitch>
        </div>
        <div className="text-right">
          <CKButton onClick={onRemove} isSmall variant="tertiary">
            <Trash className="text-gray-600" />
          </CKButton>
        </div>
      </div>

      <hr />

      <div className="w-full mt-4">
        <div className="space-y-6">
          {group.fieldMappings.map((mapping) => {
            const selectedSourceAttribute = sourceAttributes
              ? findAttribute(
                  attributeForFinder(mapping, 'source'),
                  sourceAttributes,
                )
              : null;
            const selectedTargetAttribute = targetAttributes
              ? findAttribute(
                  {
                    value: mapping.targetField,
                    objectType: objectType.name,
                    sourceType: type,
                  },
                  targetAttributes,
                )
              : null;

            const rowKey = `${mapping.targetField ||
              'empty'}:${mapping.sourceField || 'empty'}-${mapping.index}`;

            return (
              <div key={rowKey} className="mapping-group-row">
                <CKAttributeSelector
                  icon={<ServiceIcon type={selectedSourceAttribute} />}
                  isTypeMismatch={(attribute) =>
                    isTypeMismatch(attribute, selectedTargetAttribute)
                  }
                  variant={inErrorState(mapping.sourceField) && 'error'}
                  attributes={sourceAttributes}
                  onSelectedItemChange={(selected) => {
                    onChange(mapping.index)({
                      sourceField: selected?.value,
                      sourceObjectType: selected?.meta?.objectType,
                      sourceType: selected?.meta?.sourceType,
                    });
                  }}
                  placeholder="Find an attribute"
                  selectedAttribute={selectedSourceAttribute}
                  popperZIndex={30}
                />

                <DirectionForward className="text-gray" />

                <CKAttributeSelector
                  variant={inErrorState(mapping.targetField) && 'error'}
                  icon={sourceIcon(type)}
                  isTypeMismatch={(attribute) =>
                    isTypeMismatch(selectedSourceAttribute, attribute)
                  }
                  attributes={targetAttributes}
                  onSelectedItemChange={(selected) =>
                    onChange(mapping.index)({
                      targetField: selected?.value,
                      dataType: backendSupportedDataType(
                        selected?.meta?.dataType,
                      ),
                    })
                  }
                  placeholder={`Choose a ${readableType(type)} ${capitalize(
                    objectType.name,
                  )} attribute`}
                  selectedAttribute={selectedTargetAttribute}
                  popperZIndex={30}
                />
                <div>&nbsp;</div>
                <div className="pt-1 text-right">
                  <CKButton
                    onClick={onRemoveMapping(mapping.index)}
                    isSmall
                    variant="tertiary">
                    <Trash className="text-gray-600" />
                  </CKButton>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <CKButton
        isSmall
        className="mt-4 justify-self-start"
        onClick={() =>
          addMapping({
            groupId: group.groupId,
            conditionalOverwrite: group.conditionalOverwrite,
          })
        }
        variant={CKButton.Variant.tertiary}
        variantColor={CKButton.VariantColor.blue}
        leftIcon={<AddBlock />}>
        Add mapping to address group
      </CKButton>
    </CKBox>
  );
}
